import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  HStack,
  Text,
  useToast,
  Spinner,
  Center,
  Badge,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Code,
  useColorModeValue
} from '@chakra-ui/react';
import { ArrowBackIcon, ExternalLinkIcon, DownloadIcon } from '@chakra-ui/icons';
import apiClient from '../../../utils/apiClient';

const PagePreview = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [previewData, setPreviewData] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('preview'); // 'preview', 'html', 'json'

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    // First try to get preview data from sessionStorage (for live preview)
    const savedPreviewData = sessionStorage.getItem(`page-preview-${pageId}`);
    
    if (savedPreviewData) {
      try {
        const parsedData = JSON.parse(savedPreviewData);
        setPreviewData(parsedData);
        setLoading(false);
        return;
      } catch (error) {
        console.error('Error parsing preview data from sessionStorage:', error);
      }
    }

    // If no preview data in sessionStorage, fetch from API
    fetchPageForPreview();
  }, [pageId, tenantId]);

  const fetchPageForPreview = async () => {
    try {
      const response = await apiClient(`/pages/${tenantId}/${pageId}`);
      
      if (!response || response.error) {
        throw new Error(response?.error || 'Page not found');
      }

      // Get the latest version with content
      let versionWithContent = null;
      
      if (response.versions && Array.isArray(response.versions)) {
        const sortedVersions = response.versions.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        
        // Try to find a version with HTML content
        versionWithContent = sortedVersions.find(v => v.content && v.content.trim().length > 0);
        
        // If no version has content, use the latest version
        if (!versionWithContent && sortedVersions.length > 0) {
          versionWithContent = sortedVersions[0];
        }
      }

      if (!versionWithContent) {
        throw new Error('No version with content found');
      }

      setCurrentVersion(versionWithContent);
      
      // Parse layout data if needed
      let layoutData = [];
      if (versionWithContent.layout_data) {
        try {
          if (typeof versionWithContent.layout_data === 'string') {
            const firstParse = JSON.parse(versionWithContent.layout_data);
            if (typeof firstParse === 'string') {
              layoutData = JSON.parse(firstParse);
            } else {
              layoutData = firstParse;
            }
          } else {
            layoutData = versionWithContent.layout_data;
          }
        } catch (parseError) {
          console.error('Error parsing layout data:', parseError);
          layoutData = [];
        }
      }

      const previewDataFromAPI = {
        page: response,
        layout: Array.isArray(layoutData) ? layoutData : [],
        htmlContent: versionWithContent.content || '',
        completeHTML: versionWithContent.full_html || '',
        version: versionWithContent,
        hasUnsavedChanges: false
      };

      setPreviewData(previewDataFromAPI);
    } catch (error) {
      console.error('Error fetching page for preview:', error);
      toast({
        title: 'Error loading preview',
        description: error.message || 'Failed to load page preview',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadHTML = () => {
    if (!previewData?.completeHTML && !previewData?.htmlContent) {
      toast({
        title: 'No HTML content',
        description: 'No HTML content available to download',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const htmlToDownload = previewData.completeHTML || `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${previewData.page?.name || 'Page Preview'}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            line-height: 1.6;
            color: #2d3748;
            margin: 0;
            padding: 2rem;
            background-color: #ffffff;
        }
        .container {
            max-width: 1200px;
            margin: 0 auto;
        }
        * {
            box-sizing: border-box;
        }
        img {
            max-width: 100%;
            height: auto;
        }
    </style>
</head>
<body>
    <div class="container">
        ${previewData.htmlContent}
    </div>
</body>
</html>`;

    const blob = new Blob([htmlToDownload], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${previewData.page?.name || 'page'}-preview.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'HTML downloaded',
      description: 'Page HTML has been downloaded',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const openInNewTab = () => {
    if (!previewData?.completeHTML && !previewData?.htmlContent) {
      toast({
        title: 'No HTML content',
        description: 'No HTML content available to open',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const htmlToOpen = previewData.completeHTML || `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${previewData.page?.name || 'Page Preview'}</title>
</head>
<body>
    ${previewData.htmlContent}
</body>
</html>`;

    const blob = new Blob([htmlToOpen], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    
    // Clean up the URL after a delay
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  };

  if (loading) {
    return (
      <Center h="100vh">
        <VStack spacing={4}>
          <Spinner size="lg" />
          <Text>Loading preview...</Text>
        </VStack>
      </Center>
    );
  }

  if (!previewData) {
    return (
      <Center h="100vh">
        <VStack spacing={4}>
          <Text fontSize="lg">No preview data available</Text>
          <Button onClick={() => navigate(`/pages/${pageId}/builder`)}>
            Back to Builder
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box h="100vh" display="flex" flexDirection="column" bg={bgColor}>
      {/* Header */}
      <Box
        px={6}
        py={4}
        borderBottom="1px"
        borderColor={borderColor}
        bg={bgColor}
        position="sticky"
        top={0}
        zIndex={1000}
      >
        <HStack justify="space-between" align="center">
          <HStack spacing={4}>
            <Button
              leftIcon={<ArrowBackIcon />}
              variant="outline"
              onClick={() => navigate(`/pages/${pageId}/builder`)}
            >
              Back to Builder
            </Button>
            
            <VStack align="start" spacing={1}>
              <Text fontSize="lg" fontWeight="bold">
                {previewData.page?.name || 'Page Preview'}
              </Text>
              <HStack spacing={2}>
                {previewData.version && (
                  <Badge colorScheme="blue">
                    {previewData.version.version_type}
                  </Badge>
                )}
                {previewData.hasUnsavedChanges && (
                  <Badge colorScheme="orange">
                    Unsaved Changes
                  </Badge>
                )}
                {previewData.version?.created_at && (
                  <Text fontSize="xs" color="gray.500">
                    {new Date(previewData.version.created_at).toLocaleString()}
                  </Text>
                )}
              </HStack>
            </VStack>
          </HStack>

          <HStack spacing={2}>
            <Button
              leftIcon={<ExternalLinkIcon />}
              variant="outline"
              size="sm"
              onClick={openInNewTab}
            >
              Open in New Tab
            </Button>
            <Button
              leftIcon={<DownloadIcon />}
              variant="outline"
              size="sm"
              onClick={downloadHTML}
            >
              Download HTML
            </Button>
          </HStack>
        </HStack>
      </Box>

      {/* Content */}
      <Box flex={1} overflow="hidden">
        <Tabs height="100%" display="flex" flexDirection="column">
          <TabList px={6} bg={bgColor} borderBottom="1px" borderColor={borderColor}>
            <Tab>Live Preview</Tab>
            <Tab>HTML Source</Tab>
            <Tab>JSON Data</Tab>
          </TabList>
          
          <TabPanels flex={1} overflow="hidden">
            {/* Live Preview Tab */}
            <TabPanel p={0} height="100%" overflow="auto">
              <Box height="100%">
                {previewData.htmlContent ? (
                  <Box
                    height="100%"
                    dangerouslySetInnerHTML={{
                      __html: previewData.htmlContent
                    }}
                    sx={{
                      '& *': {
                        maxWidth: '100%',
                      },
                      '& img': {
                        maxWidth: '100%',
                        height: 'auto',
                      }
                    }}
                    p={6}
                  />
                ) : (
                  <Center height="100%">
                    <VStack spacing={4}>
                      <Text fontSize="lg" color="gray.500">
                        No HTML content available
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        Save the page to generate HTML content
                      </Text>
                    </VStack>
                  </Center>
                )}
              </Box>
            </TabPanel>

            {/* HTML Source Tab */}
            <TabPanel p={0} height="100%" overflow="auto">
              <Box p={4} height="100%">
                {previewData.completeHTML || previewData.htmlContent ? (
                  <Code
                    display="block"
                    whiteSpace="pre-wrap"
                    fontSize="sm"
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    width="100%"
                    height="100%"
                    overflow="auto"
                  >
                    {previewData.completeHTML || previewData.htmlContent}
                  </Code>
                ) : (
                  <Center height="100%">
                    <Text color="gray.500">No HTML content available</Text>
                  </Center>
                )}
              </Box>
            </TabPanel>

            {/* JSON Data Tab */}
            <TabPanel p={0} height="100%" overflow="auto">
              <Box p={4} height="100%">
                <Code
                  display="block"
                  whiteSpace="pre-wrap"
                  fontSize="sm"
                  p={4}
                  bg="gray.50"
                  borderRadius="md"
                  width="100%"
                  height="100%"
                  overflow="auto"
                >
                  {JSON.stringify({
                    page: previewData.page,
                    layout: previewData.layout,
                    version: previewData.version,
                    component_count: previewData.layout?.length || 0
                  }, null, 2)}
                </Code>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
};

export default PagePreview;
